import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { PageGallery } from "@/components/PageGallery";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import {
  NOTARY_PROVINCE_LANDINGS,
  getNotaryProvinceLanding,
  type NotaryProvinceLanding,
} from "@/content/notary-corpus/provinces";
import { NOTARY_DOC_LANDINGS } from "@/content/notary-corpus/documents";

function localFaqsTh(p: NotaryProvinceLanding) {
  return [
    {
      q: `หาทนายรับรองเอกสาร (Notary Public) ใน${p.provinceTh} ได้ที่ไหน?`,
      a: `${p.logisticsTh} ทนายที่รับรองเป็นทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (Notarial Services Attorney) ซึ่งขึ้นทะเบียนกับสภาทนายความในพระบรมราชูปถัมภ์ตามข้อบังคับ พ.ศ. 2551 ตรวจสอบสถานะใบอนุญาตได้ก่อนใช้บริการทุกครั้ง`,
    },
    {
      q: `เอกสารอะไรที่คน${p.provinceTh}ขอให้ทนายรับรองบ่อยที่สุด?`,
      a: p.topDocsTh.join(" · "),
    },
    { q: `ใช้เวลานานเท่าไหร่เมื่ออยู่${p.provinceTh}?`, a: p.turnaroundTh },
    {
      q: `ค่าบริการรับรองเอกสารใน${p.provinceTh} คิดอย่างไร?`,
      a: "คิดตามจำนวนฉบับและจำนวนตราประทับที่ต้องใช้ หากมีค่าธรรมเนียมนิติกรณ์กรมการกงสุลหรือค่าธรรมเนียมสถานทูตปลายทาง จะคิดตามอัตราทางการของหน่วยงานนั้นแยกจากค่าบริการ · ขอใบเสนอราคาฟรีก่อนเริ่มงานได้ทุกครั้ง",
    },
    {
      q: "ต้องมาเซ็นเองไหม หรือส่งเอกสารที่เซ็นแล้วมาได้?",
      a: "การรับรองลายมือชื่อต้องลงนามต่อหน้าทนายด้วยตนเองพร้อมหนังสือเดินทางหรือบัตรประชาชนตัวจริง เอกสารที่เซ็นมาจากบ้านแล้วต้องเซ็นใหม่ ส่วนการรับรองสำเนาถูกต้องต้องนำต้นฉบับมาแสดง",
    },
    {
      q: `รับรองที่${p.provinceTh}แล้วต้องเข้ากรุงเทพฯ เพื่อทำนิติกรณ์เองไหม?`,
      a: "ไม่จำเป็น เราสามารถรับมอบอำนาจนำเอกสารเข้ายื่นกองสัญชาติและนิติกรณ์ กรมการกงสุล ที่แจ้งวัฒนะ และประสานสถานทูตปลายทางแทนได้ แล้วส่งเอกสารกลับทางไปรษณีย์ลงทะเบียน/ขนส่งเอกชน",
    },
    {
      q: "ประเทศไทยออก Apostille ได้หรือยัง?",
      a: "ยังไม่มีผลบังคับใช้ในทางปฏิบัติครับ ประเทศไทยยื่นภาคยานุวัติสารเข้าเป็นภาคีอนุสัญญากรุงเฮกว่าด้วยการยกเลิกการรับรองเอกสาร (Apostille Convention) เมื่อ 30 มิถุนายน 2569 (2026) และอนุสัญญาจะมีผลกับไทยวันที่ 28 กุมภาพันธ์ 2570 (2027) ระหว่างนี้เอกสารไทยที่จะใช้ต่างประเทศยังต้องผ่านนิติกรณ์กรมการกงสุล กระทรวงการต่างประเทศ แล้วรับรองต่อที่สถานทูต/สถานกงสุลของประเทศปลายทาง — โปรดตรวจสถานะล่าสุดที่ hcch.net และ consular.mfa.go.th ก่อนดำเนินการ",
    },
    {
      q: "ทนายรับรองเอกสารในไทยเทียบเท่า Notary Public ต่างประเทศไหม?",
      a: "ไทยไม่มีตำแหน่ง Notary Public ตามระบบ common law แต่มี 'ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร' ซึ่งเป็นผู้รับรองที่หน่วยงานและสถานทูตต่างประเทศยอมรับ เมื่อผ่านนิติกรณ์กรมการกงสุลต่อ",
    },
    {
      q: "รับประกันว่าปลายทางจะรับเอกสารไหม?",
      a: "ไม่ การยอมรับเอกสารเป็นดุลพินิจของหน่วยงานปลายทาง เราตรวจรูปแบบ ตัวสะกดชื่อตามหนังสือเดินทาง อายุเอกสาร และลำดับขั้นการรับรองก่อนดำเนินการ และแก้ไขให้ใหม่โดยไม่คิดค่าบริการหากข้อผิดพลาดเกิดจากฝั่งเรา",
    },
  ];
}

export const Route = createFileRoute("/notary/province/$province")({
  loader: ({ params }) => {
    const p = getNotaryProvinceLanding(params.province);
    if (!p) throw notFound();
    return { p };
  },
  component: NotaryProvincePageTH,
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "ไม่พบจังหวัด" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { p } = loaderData;
    const title = `ทนายรับรองเอกสาร Notary Public ${p.provinceTh} 2026`;
    const description = `ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสารใน${p.provinceTh} — รับรองหนังสือมอบอำนาจ Affidavit สำเนาถูกต้อง พร้อมเดินเรื่องนิติกรณ์กรมการกงสุลและสถานทูตปลายทาง`;
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: `/notary/province/${params.province}`,
        type: "article",
        alternates: [{ lang: "en", path: `/en/notary/province/${params.province}` }],
        keywords: [
          `ทนายรับรองเอกสาร ${p.provinceTh}`,
          `notary public ${p.provinceTh}`,
          `ทนายรับรองลายมือชื่อ ${p.provinceTh}`,
          `รับรองหนังสือมอบอำนาจ ${p.provinceTh}`,
          `notarial services attorney ${p.provinceTh}`,
        ],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: `${site}/` },
              { name: "ทนาย Notary Public", url: `${site}/notary` },
              { name: `Notary Public ${p.provinceTh}`, url: `${site}/notary/province/${params.province}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(localFaqsTh(p))) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/notary/province/${params.province}`)) },
      ],
    };
  },
});

function NotaryProvincePageTH() {
  const { p } = Route.useLoaderData();
  const faqs = localFaqsTh(p);
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <a href="/notary" className="hover:underline">ทนาย Notary Public</a> <span>/</span>{" "}
          <span>{p.provinceTh}</span>
        </nav>

        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          ทนายรับรองเอกสาร (Notary Public) {p.provinceTh} — Notarial Services Attorney 2026
        </h1>
        <p className="mb-6 text-lg text-muted-foreground" data-speakable="true">{p.demandTh}</p>

        <div className="mb-8 flex flex-wrap gap-2 text-sm">
          <a href="/contact" className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground">ขอใบเสนอราคาฟรี →</a>
          <a href="/notary/faq" className="rounded-md border px-4 py-2 font-medium hover:bg-muted">คำถามที่พบบ่อย Notary</a>
          <a href={`/en/notary/province/${p.slug}`} className="rounded-md border px-4 py-2 text-muted-foreground hover:bg-muted">English →</a>
        </div>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">เอกสารที่คน{p.provinceTh}ขอรับรองบ่อยที่สุด</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {p.topDocsTh.map((d: string, i: number) => <li key={i}>{d}</li>)}
          </ul>
        </section>

        <section className="mb-10 rounded-lg border bg-card p-6">
          <h2 className="mb-3 text-xl font-bold">นัดหมายและรับ-ส่งเอกสารใน{p.provinceTh}</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{p.logisticsTh}</p>
          <p className="mt-4 text-sm text-muted-foreground"><strong>ระยะเวลา:</strong> {p.turnaroundTh}</p>
        </section>

        <section className="mb-10 rounded-lg border bg-muted/20 p-6">
          <h2 className="mb-3 text-xl font-bold">ลำดับขั้นการรับรองเอกสารไทยเพื่อใช้ต่างประเทศ</h2>
          <ol className="list-decimal space-y-1 pl-6 text-sm text-muted-foreground">
            <li>ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสารรับรองลายมือชื่อ/สำเนาถูกต้อง (ต้องลงนามต่อหน้า)</li>
            <li>แปลเป็นภาษาปลายทางโดยผู้แปลที่หน่วยงานปลายทางยอมรับ (ถ้าจำเป็น)</li>
            <li>นิติกรณ์กองสัญชาติและนิติกรณ์ กรมการกงสุล กระทรวงการต่างประเทศ (แจ้งวัฒนะ)</li>
            <li>รับรองต่อที่สถานทูต/สถานกงสุลของประเทศปลายทางในไทย ตามที่ปลายทางกำหนด</li>
          </ol>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">คำถามที่พบบ่อย — Notary Public {p.provinceTh}</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="rounded-lg border bg-card p-4" {...(i < 3 ? { open: true } : {})}>
                <summary className="cursor-pointer text-sm font-semibold">{f.q}</summary>
                <p className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">รับรองเอกสารตามประเภท</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {NOTARY_DOC_LANDINGS.map((x) => (
              <a key={x.slug} href={`/notary/document/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {x.titleTh}
              </a>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">พื้นที่ให้บริการอื่น</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {NOTARY_PROVINCE_LANDINGS.filter((x) => x.slug !== p.slug).map((x) => (
              <a key={x.slug} href={`/notary/province/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {x.provinceTh}
              </a>
            ))}
          </div>
        </section>

        <p className="mb-6 text-xs text-muted-foreground">
          กรอบวิชาชีพอ้างอิง: ข้อบังคับสภาทนายความว่าด้วยการขึ้นทะเบียนทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร พ.ศ. 2551 ·
          ขั้นตอนนิติกรณ์อ้างอิงกองสัญชาติและนิติกรณ์ กรมการกงสุล (consular.mfa.go.th) · สถานะภาคีอนุสัญญากรุงเฮก (hcch.net) —
          ค่าธรรมเนียมและระยะเวลาอาจเปลี่ยนแปลง โปรดตรวจสอบกับหน่วยงานเจ้าของเรื่องก่อนดำเนินการทุกครั้ง
        </p>

        <PageGallery category="notary" seed="notary-province" locale="th" />
        <FaqSourceNote topic="notary" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}
